/* @flow */
/**
 * Redux Toolkit slice for conversation category code configuration.
 * Loads active conversation codes and sends add/update requests for configuration changes.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import { toggleSpinner } from '../dialogStates/dialogStatesSlice';
import { cryptoRand } from '../../helpers/common';
import { showForFPC } from '../../helpers/envServer';

export const name = 'conversationCategoryCodeConfig';

function createInitialState() {
  return {
    dataId: '',
    dataRows: []
  };
}

export const initialState = createInitialState();

/**
 * Reducer that stores conversation category code rows and refreshes the data id.
 */
function setConversationCategoryCodes(state: Object, action: Object) {
  // SET_CONVERSATION_CATEGORY_CODES

  // Refresh the id so grids depending on dataId know the row set changed.
  const dataId = cryptoRand().toString().substr(2, 8);
  const dataRows = action.payload.objects;

  return {
    ...state,
    dataRows,
    dataId
  };
}

/**
 * Reducer placeholder for add conversation category code state updates.
 */
function addConversationCategoryCodes(state: Object) {
  // ADD_CONVERSATION_CATEGORY_CODES

  return state;
}

/**
 * Reducer placeholder for update conversation category code state updates.
 */
function updateConversationCategoryCodes(state: Object) {
  // UPDATE_CONVERSATION_CATEGORY_CODES

  return state;
}

/**
 * Builds the reducer map consumed by the conversation category code slice.
 */
function createReducers() {
  return {
    setConversationCategoryCodes,
    addConversationCategoryCodes,
    updateConversationCategoryCodes
  };
}

export const reducers = createReducers();
export const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };

/**
 * Async thunk that calls getConversationCategoryCodes for active code records.
 */
function callGetConversationCodeAPI() {
  return createAsyncThunk(
    `${name}/callGetConversationCodeAPI`,
    async (
      { callback },
      { getState, dispatch }
    ) => {
      // Limit the lookup to fields required by the configuration grid.
      const fields = 'ConversationCode,ConversationDescription,modifiedBy,modifiedDate,isActive,id,conversationCategory';
      // Only active conversation codes are shown in the configuration UI.
      const filters = 'isActive(true)';
      const query = `?fields=${fields}&filter=${filters}`;
      dispatch(toggleSpinner(true));
      request(
        {
          api: 'getConversationCategoryCodes',
          routeParams: { query },
          method: 'get'
        },
        dispatch,
        getState
      )
        .then((response) => {
          dispatch(toggleSpinner(false));
          dispatch(actions.setConversationCategoryCodes(response.data));
          if (callback) callback(response.data);
        })
        .catch((error) => {
          console.log('callGetConversationCodeAPI->error', error);
          dispatch(toggleSpinner(false));
          if (callback) callback(error);
        });
    }
  );
}

/**
 * Async thunk that posts new conversation codes to addConversationCodes.
 */
function callAddConversationCodeAPI() {
  return createAsyncThunk(
    `${name}/callAddConversationCodeAPI`,
    async (
      {
        payload,
        callback,
        selectedRule,
        hasFPCAccess
      },
      { getState, dispatch }
    ) => {
      const inputList = [];
      console.log('ignore', selectedRule);
      // Build the API payload from editable grid rows.
      payload.forEach((row, index) => {
        const element = {};
        element.ConversationCode = payload[index].ConversationCode;
        // Checked category rows are stored as reject codes with a product-specific prefix.
        if (row.conversationCategory === true) {
          element.conversationCategory = 'REJECT';
          const ConversationDescription = hasFPCAccess && showForFPC ? 'Doc Reject - '.concat(payload[index].ConversationDescription)
            : 'PO Reject - '.concat(payload[index].ConversationDescription);
          // Normalize repeated whitespace before sending descriptions to the API.
          const cleanedString = ConversationDescription.replace(/\s+/g, ' ').trim();
          element.ConversationDescription = cleanedString;
        } else {
          element.conversationCategory = 'Other';
          const { ConversationDescription } = payload[index];
          const cleanedString = ConversationDescription.replace(/\s+/g, ' ').trim();
          element.ConversationDescription = cleanedString;
        }
        inputList.push(element);
      });
      dispatch(toggleSpinner(true));
      request(
        {
          api: 'addConversationCodes',
          method: 'post',
          data: { conversationData: inputList }
        },
        dispatch,
        getState
      )
        .then((response) => {
          dispatch(toggleSpinner(false));
          if (callback) callback(response.data);
        })
        .catch((error) => {
          console.log('callAddConversationCodeAPI->error', error);
          dispatch(toggleSpinner(false));
          if (callback) callback(error);
        });
    }
  );
}

/**
 * Async thunk that sends conversation code updates to updateConversationCodes.
 */
function callUpdateConversationCodeAPI() {
  return createAsyncThunk(
    `${name}/callUpdateConversationCodeAPI`,
    async (
      { payload, callback },
      { getState, dispatch }
    ) => {
      dispatch(toggleSpinner(true));
      request(
        {
          api: 'updateConversationCodes',
          method: 'put',
          data: {
            // The update endpoint expects the operation type alongside the changed rows.
            type: 'update',
            conversationData: payload
          }
        },
        dispatch,
        getState
      )
        .then((response) => {
          dispatch(toggleSpinner(false));
          if (callback) callback(response.data);
        })
        .catch((error) => {
          console.log('callUpdateConversationCodeAPI error', error);
          dispatch(toggleSpinner(false));
          if (callback) callback(error);
        });
    }
  );
}

/**
 * Creates async thunk action creators exposed by the conversation category code slice.
 */
function createExtraActions() {
  return {
    callGetConversationCodeAPI: callGetConversationCodeAPI(),
    callAddConversationCodeAPI: callAddConversationCodeAPI(),
    callUpdateConversationCodeAPI: callUpdateConversationCodeAPI()
  };
}

export const extraActions = createExtraActions();

export const conversationCategoryCodeConfigActions = { ...actions, ...extraActions };
export const conversationCategoryCodeConfigReducer = slice.reducer;
